import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { useMemo } from 'react';

import { adaptColumnsToTanStack, getMergeByColumnKeys } from '../adapters/columnAdapter';
import { buildColumnMap, getRowSpanConfiguration } from '../helpers';
import type { DataWithRowId, RowSpanConfiguration, TableColumn } from '../types';
import { hiddenColumnsToVisibilityState } from '../utils/columnStateConverters';

interface UseTableColumnsProps<T extends object> {
  columnOrder: string[];
  columns: TableColumn<T>[];
  data: T[];
  hiddenColumns: string[];
}

interface UseTableColumnsReturn<T extends object, K extends object = T> {
  columnMapping: Record<string, TableColumn<T>>;
  columnVisibility: VisibilityState;
  mergeByColumnKeys: string[];
  orderedColumns: TableColumn<T>[];
  rowSpanConfiguration: RowSpanConfiguration;
  tanstackColumns: ColumnDef<DataWithRowId<T, K>, unknown>[];
  visibleColumns: TableColumn<T>[];
}

export const useTableColumns = <T extends object, K extends object = T>({
  columns,
  columnOrder,
  data,
  hiddenColumns
}: UseTableColumnsProps<T>): UseTableColumnsReturn<T, K> => {
  // Build column mapping for quick lookup by key
  const columnMapping: Record<TableColumn['key'], TableColumn<T>> = useMemo(() => buildColumnMap<T>(columns), [columns]);

  // Compute visibility state from hiddenColumns prop for TanStack
  const columnVisibility: VisibilityState = useMemo(() => hiddenColumnsToVisibilityState(hiddenColumns), [hiddenColumns]);

  // Get ordered columns for passing to TanStack (ALL columns, not filtered)
  const orderedColumns: TableColumn<T>[] = useMemo(() => {
    return columnOrder.map((key: string) => columnMapping[key]).filter(Boolean);
  }, [columnOrder, columnMapping]);

  // Compute visible columns for fixed column offset calculation
  // This filters orderedColumns by those not in hiddenColumns
  const visibleColumns: TableColumn<T>[] = useMemo(
    () => orderedColumns.filter(({ key }) => !hiddenColumns.includes(key)),
    [orderedColumns, hiddenColumns]
  );

  // Memoize row span configuration to avoid recomputing on every render
  const rowSpanConfiguration = useMemo(() => getRowSpanConfiguration(data, columns), [data, columns]);

  const mergeByColumnKeys = useMemo(() => getMergeByColumnKeys(columns), [columns]);

  // Adapt ALL columns to TanStack format - TanStack will handle visibility via columnVisibility state
  const tanstackColumns = useMemo(
    () => adaptColumnsToTanStack<T, DataWithRowId<T, K>>(orderedColumns, rowSpanConfiguration, mergeByColumnKeys),
    [orderedColumns, rowSpanConfiguration, mergeByColumnKeys]
  );

  return {
    columnMapping,
    columnVisibility,
    orderedColumns,
    visibleColumns,
    rowSpanConfiguration,
    mergeByColumnKeys,
    tanstackColumns
  };
};
